/**
 * Jersey Resolver - Pure Utility Functions
 *
 * Centralized logic for resolving team jersey URLs.
 * This eliminates duplicated logic across TeamPick, PlayerPick, and PropPick components.
 */

/**
 * Team interface for jersey resolution
 */
export interface Team {
  id: string;
  name: string;
  color: string;
  accent: string;
  jerseyUrl?: string | null;
  thirdImageUrl?: string | null;
  popularityPct?: number;
  position?: string; // Player position (QB, WR, etc.)
  fixture?: {
    homeTeam: string;
    awayTeam: string;
    matchTime: string; // Formatted (e.g., "SUN, 1:00pm")
  };
  isPlayerTeam?: 'home' | 'away'; // Which team the player belongs to
  liveValue?: number; // Live stat value from grading (for selection questions)
  liveStatType?: string; // Display name for stat type (e.g., "Points", "Rushing Yards")
}

/**
 * Resolves the jersey URL for a team with consistent fallback logic
 *
 * Priority order:
 * 1. API-provided jerseyUrl
 * 2. Empty string (no image displayed)
 *
 * @param team - The team object with optional jerseyUrl
 * @returns URL string for the jersey image, or empty string if not available
 */
export function resolveJerseyUrl(team: Team): string {
  // Use jerseyUrl from API if available, otherwise return empty string
  return team.jerseyUrl || '';
}

/**
 * Creates a Map of team IDs to jersey URLs for efficient lookup
 * Useful when rendering multiple teams
 *
 * @param teams - Array of teams
 * @returns Map with team.id as key and jersey URL as value
 */
export function createJerseyMap(teams: Team[]): Map<string, string> {
  const jerseyMap = new Map<string, string>();

  teams.forEach(team => {
    jerseyMap.set(team.id, resolveJerseyUrl(team));
  });

  return jerseyMap;
}

/**
 * Resolves jerseys for exactly two teams (common case)
 * Returns an object with team1Jersey and team2Jersey properties
 *
 * @param team1 - First team
 * @param team2 - Second team
 * @returns Object with jersey URLs for both teams
 */
export function resolveJerseyPair(
  team1: Team,
  team2: Team
): { team1Jersey: string; team2Jersey: string } {
  return {
    team1Jersey: resolveJerseyUrl(team1),
    team2Jersey: resolveJerseyUrl(team2),
  };
}
